const asArray = (value) => {
    if (value === null || value === undefined || value === '' || value === 'all') return [];
    return Array.isArray(value) ? value : [value];
};

const numericIds = (value) => asArray(value)
    .map(Number)
    .filter(Number.isFinite);

const compactSelection = (value) => {
    const ids = numericIds(value);
    if (ids.length === 0) return 'all';
    return ids.length === 1 ? ids[0] : ids;
};

const normaliseName = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const findAmenity = (amenities, names) => {
    const accepted = new Set(names.map(normaliseName));
    return (amenities || []).find(item => accepted.has(normaliseName(item.name)));
};

const putAmenity = (target, definition, value) => {
    if (!definition || value === null || value === undefined) return;
    target[definition.id] = value;
};

/** Convert the cumulative action.match contract into FilterPanel state. */
export function filtersFromAiMatch(previousFilters, match, lookups = {}) {
    if (!match) return previousFilters;

    const amenities = lookups.amenities || [];
    const geoFeatures = lookups.geoFeatures || [];
    const nextAmenities = {};
    const nextGeoFeatures = {};

    const bedroom = findAmenity(amenities, ['no_bedroom', 'bedroom', 'bedrooms']);
    if (bedroom && (match.min_no_bedroom != null || match.max_no_bedroom != null)) {
        putAmenity(nextAmenities, bedroom, [
            match.min_no_bedroom ?? bedroom.min_value ?? 0,
            match.max_no_bedroom ?? bedroom.max_value ?? 20,
        ]);
    }

    [
        ['parking', ['parking']],
        ['elevator', ['elevator']],
        ['storage', ['storage']],
        ['balcony', ['balcony']],
        ['freight_elevator', ['freight_elevator', 'cargo_elevator']],
    ].forEach(([field, aliases]) => {
        // The current FilterPanel boolean control represents a positive
        // requirement. OpenSearch remains authoritative for explicit false.
        if (Number(match[field]) === 1) {
            putAmenity(nextAmenities, findAmenity(amenities, aliases), true);
        }
    });

    const floor = findAmenity(amenities, ['floor', 'no_floor']);
    if (floor && Array.isArray(match.floor) && match.floor.length > 0) {
        const floors = match.floor.map(Number).filter(Number.isFinite);
        putAmenity(
            nextAmenities,
            floor,
            floor.value_type === 'numerical'
                ? [Math.min(...floors), Math.max(...floors)]
                : floors,
        );
    }

    // Required HCM features become visible filters. Preferred features remain
    // ranking-only and are represented by the OpenSearch score/order.
    (match.amenities || []).forEach(item => {
        if (!item.required) return;
        const definition = amenities.find(a => Number(a.hcm_feature_id) === Number(item.amenity_id));
        putAmenity(nextAmenities, definition, item.value ?? true);
    });
    (match.geo_features || []).forEach(item => {
        if (!item.required || item.feature_id == null) return;
        const definition = geoFeatures.find(f => Number(f.hcm_feature_id) === Number(item.feature_id));
        if (definition) nextGeoFeatures[definition.id] = item.value ?? true;
    });

    const previousNeighborhoods = new Map(
        (previousFilters.geoFilters || [])
            .filter(item => item.type === 'neighborhood')
            .map(item => [Number(item.id), item]),
    );
    const geoFilters = numericIds(match.candidate_neighborhood_ids).map(id => (
        previousNeighborhoods.get(id) || { type: 'neighborhood', id, name: `#${id}` }
    ));

    return {
        ...previousFilters,
        listingType: match.purpose === 'RENT' ? 'rent' : 'sale',
        propertyType: compactSelection(match.property_type_id),
        usageType: compactSelection(match.usage_id),
        minArea: Number(match.min_area_sqm) || 0,
        maxArea: Number(match.max_area_sqm) || 0,
        maxPrice: match.purpose === 'BUY' ? Number(match.purchase_budget_max) || 0 : 0,
        totalBudget: match.purpose === 'RENT' ? Number(match.total_budget) || 0 : 0,
        depositBudgetMax: match.purpose === 'RENT' ? Number(match.deposit_budget_max) || 0 : 0,
        rentBudgetMax: match.purpose === 'RENT' ? Number(match.rent_budget_max) || 0 : 0,
        geoFilters,
        amenities: nextAmenities,
        geoFeatures: nextGeoFeatures,
        aiCandidateHexIds: numericIds(match.candidate_hex_ids),
    };
}

/** Build the canonical ranked result set from one chatbot response. */
export function aiMatchFromResponse(payload) {
    const criteria = payload?.action?.match;
    const matches = payload?.matches;
    if (!criteria || !matches || matches.error) return null;

    const results = Array.isArray(matches.results)
        ? matches.results.map((item, index) => ({
            ...item,
            listing_id: Number(item.listing_id),
            rank: Number(item.rank) || index + 1,
        })).filter(item => Number.isFinite(item.listing_id))
        : [];

    return {
        active: true,
        criteria,
        results,
        total: Number(matches.total) || 0,
        page: Number(matches.page) || 1,
        pageSize: Number(matches.page_size) || results.length,
        revision: `${payload.session_id || 'chat'}-${Date.now()}`,
    };
}

export function rankListingsByAiMatch(listings, aiMatch) {
    if (!aiMatch?.active) return listings;
    const resultById = new Map(
        aiMatch.results.map(item => [Number(item.listing_id), item]),
    );
    return listings
        .filter(listing => resultById.has(Number(listing.listing_id)))
        .map(listing => {
            const result = resultById.get(Number(listing.listing_id));
            return { ...listing, ai_match_rank: result.rank, ai_match_score: result.score };
        })
        .sort((a, b) => a.ai_match_rank - b.ai_match_rank);
}

export function selectionContains(selection, id) {
    return numericIds(selection).includes(Number(id));
}
